import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import CustomSelect from "../../../common/formfields/CustomSelect";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const NoticeForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const isEditMode = Boolean(id);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEditMode);
    const [siteId, setSiteId] = useState(null);

    const [formData, setFormData] = useState({
        newsTitle: '',
        discription: '',
        isActiveId: 1
    });

    const employeeCode = storageService.getItem("employeeCode");
    const roleId = Number(storageService.getItem("roleId"));

    useEffect(() => {
        const initialize = async () => {
            let currentSiteId = null;
            if (employeeCode) {
                try {
                    const userRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { userId: employeeCode }
                    });
                    if (userRes.success && userRes.data?.data?.[0]) {
                        currentSiteId = userRes.data.data[0].siteId;
                        setSiteId(currentSiteId);
                    }
                } catch (err) {
                    console.error("Error fetching user details", err);
                }
            }

            if (isEditMode) {
                if (location.state && location.state.item) {
                    const item = location.state.item;
                    setFormData({
                        newsTitle: item.newsTitle,
                        discription: item.discription,
                        isActiveId: item.status === 'Active' ? 1 : 0
                    });
                    setLoading(false);
                } else if (currentSiteId) {
                    try {
                        const res = await apiServices({
                            url: `/DailyNews/GetDailyNewsBySiteId/${currentSiteId}`,
                            method: 'GET'
                        });
                        if (res.success && res.data?.IsSuccess && Array.isArray(res.data.dailyNews)) {
                            const notice = res.data.dailyNews.find(n => n.Id === Number(id));
                            if (notice) {
                                setFormData({
                                    newsTitle: notice.newsTitle,
                                    discription: notice.discription,
                                    isActiveId: notice.IsActive === 'Active' ? 1 : 0
                                });
                            } else {
                                toast.error("Failed to load notice details.");
                                navigate("/zentres/News-Letters", { state: { activeTab: 'notices' } });
                            }
                        }
                    } catch (err) {
                        toast.error("Failed to load notice details");
                    } finally {
                        setLoading(false);
                    }
                }
            }
        };

        initialize();
    }, [id, isEditMode, location.state, employeeCode, navigate]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (id, option) => {
        const val = option?.value;
        setFormData(prev => ({ ...prev, [id]: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!siteId) {
            toast.error("Site ID is missing. Please reload the page.");
            return;
        }

        if (!formData.newsTitle.trim() || !formData.discription.trim()) {
            toast.error("Please provide both Title and Description.");
            return;
        }

        setSubmitting(true);
        try {
            const body = {
                siteId: siteId,
                newsTitle: formData.newsTitle,
                discription: formData.discription,
                createdBy: roleId,
                dailyNews: '',
                isActiveId: formData.isActiveId
            };

            const url = isEditMode 
                ? `/DailyNews/UpdateDailyNews/${id}`
                : `/DailyNews/postDailyNews`;
            
            const res = await apiServices({
                url,
                method: isEditMode ? 'PUT' : 'POST',
                data: body
            });

            if (res.success && res.data?.IsSuccess !== false) {
                toast.success(`Notice ${isEditMode ? 'updated' : 'added'} successfully!`);
                navigate('/zentres/News-Letters', { state: { activeTab: 'notices' } }); 
            } else {
                toast.error("Failed to save notice.");
            }
        } catch (error) {
            console.error("Error saving notice:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={isEditMode ? "Edit Notice" : "Add Notice"}
                subHeading={`Please fill out the form below to ${isEditMode ? 'update the existing' : 'create a new'} notice.`}
            />
            
            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Title */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="newsTitle">Title <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                id="newsTitle"
                                value={formData.newsTitle}
                                onChange={handleInputChange}
                                placeholder="Enter notice title"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                required
                            />
                        </div>

                        {/* Status */}
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 mb-1 block uppercase tracking-wider">Status <span className="text-red-500">*</span></span>}
                                options={[
                                    { label: 'Active', value: 1 },
                                    { label: 'Inactive', value: 0 }
                                ]}
                                value={{ label: formData.isActiveId === 1 ? 'Active' : 'Inactive', value: formData.isActiveId }}
                                onChange={(opt) => handleSelectChange("isActiveId", opt)}
                                placeholder="Select Status"
                            />
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="discription">Description <span className="text-red-500">*</span></label>
                            <textarea
                                id="discription"
                                value={formData.discription}
                                onChange={handleInputChange}
                                placeholder="Enter description"
                                rows="5"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 resize-none"
                                required
                            />
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6 mb-8">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/News-Letters", { state: { activeTab: 'notices' } })}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Notice' : 'Save Notice')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoticeForm;
